import { type FC, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { viewMore, viewLess } from '../../finding/sbMessages.tsx';
import { PageLayout } from 'dibs-elements/exports/PageLayout.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { QuickViewProvider } from 'dibs-search-product-tile/exports/QuickViewProvider.ts';
import { useViewMore } from '../../finding/hooks/viewMore/useViewMore.ts';
import { locations } from '../../utils/tracking/shared/favoritesTracking.tsx';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider.tsx';
import { SharedItemTile } from '../SharedItemTile/SharedItemTile.tsx';
import { useSbSharedItemTracking } from '../../finding/hooks/sharedItemTracking/useSbSharedItemTracking.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import classnames from 'classnames';

import dibsCss from 'dibs-css';
import styles from './SharedRelatedItemsSection.scss';

import { type SharedRelatedItemsSection_viewer$key } from './__generated__/SharedRelatedItemsSection_viewer.graphql.ts';
import { type SharedRelatedItemsSection_itemSearch$key } from './__generated__/SharedRelatedItemsSection_itemSearch.graphql.ts';
import { type SharedRelatedItemsSection_user$key } from './__generated__/SharedRelatedItemsSection_user.graphql.ts';
import { type SharedRelatedItemsSection_item$key } from './__generated__/SharedRelatedItemsSection_item.graphql.ts';

const viewerFragment = graphql`
    fragment SharedRelatedItemsSection_viewer on Viewer {
        ...useSbSharedItemTracking_viewer
        ...SharedItemTile_viewer
    }
`;

const itemSearchFragment = graphql`
    fragment SharedRelatedItemsSection_itemSearch on ItemSearchQueryConnection {
        ...useSbSharedItemTracking_itemSearch
        ...SharedItemTile_itemSearch
    }
`;

const userFragment = graphql`
    fragment SharedRelatedItemsSection_user on User {
        serviceId
        ...SharedItemTile_user
    }
`;

const itemsFragment = graphql`
    fragment SharedRelatedItemsSection_item on Item @relay(plural: true) {
        serviceId
        ...FavoritesProvider_item
        ...useSbSharedItemTracking_item
        ...SharedItemTile_item
            @arguments(isTrade: $isTrade, priceBookName: $priceBookName, showSeller: $showSeller)
    }
`;

type Props = {
    viewer: SharedRelatedItemsSection_viewer$key;
    itemSearch: SharedRelatedItemsSection_itemSearch$key;
    items: SharedRelatedItemsSection_item$key;
    user: SharedRelatedItemsSection_user$key | null | undefined;
    initialRowCount: number;
    header: ReactNode;
    hideRecentSoldPrice?: boolean;
    list: string;
};

export const SharedRelatedItemsSection: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    items: itemsRef,
    user: userRef,
    initialRowCount,
    header,
    hideRecentSoldPrice,
    list,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);
    const items = useFragment(itemsFragment, itemsRef);

    const { afterDisplay } = useSbSelector(state => state.relayVariables.variables);
    const { isMobile } = useServerVarsContext();

    const totalCount = items?.length;
    const itemsPerRow = isMobile ? 2 : 4;
    const initialCount = initialRowCount * itemsPerRow;

    const {
        viewMoreEl,
        dispatch: toggleViewMore,
        visibleCount,
    } = useViewMore<HTMLButtonElement>({
        totalCount,
        revealCount: totalCount - initialCount,
        initialCount,
    });
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch,
        items,
        list,
    });

    if (!items.length) {
        return null;
    }

    return (
        <FavoritesProvider
            disable={!afterDisplay}
            items={items}
            location={locations.SEARCH_BROWSE}
            userId={user?.serviceId}
            fetchFolder={!isMobile}
        >
            {({ props: favorites }) => (
                <QuickViewProvider>
                    <PageLayout setOffset={false}>
                        <div
                            className={classnames(
                                dibsCss.mtLarge,
                                dibsCss.borderT,
                                dibsCss.borderTSolid,
                                dibsCss.borderTDolphin
                            )}
                        >
                            <HeadingLevel>
                                {Heading => (
                                    <Heading
                                        className={classnames(
                                            styles.header,
                                            dibsCss.textCenter,
                                            dibsCss.mtLarge,
                                            dibsCss.mbSmall
                                        )}
                                    >
                                        {header}
                                    </Heading>
                                )}
                            </HeadingLevel>
                            <div
                                className={classnames(
                                    styles.gridContainer,
                                    dibsCss.grid,
                                    dibsCss.gapSmall
                                )}
                            >
                                {items.map((item, index) => (
                                    <SharedItemTile
                                        key={item.serviceId}
                                        index={index}
                                        viewer={viewer}
                                        item={item}
                                        itemSearch={itemSearch}
                                        user={user}
                                        fetchFolderOnMouseOver={!isMobile}
                                        lazyLoadStartIndex={initialCount}
                                        onClick={() =>
                                            fireItemClickTracking({
                                                itemId: item.serviceId,
                                                index,
                                            })
                                        }
                                        onContentVisible={() =>
                                            fireItemImpressionTracking({
                                                itemId: item.serviceId,
                                                index,
                                            })
                                        }
                                        favorites={favorites}
                                        hideRecentSoldPrice={hideRecentSoldPrice}
                                        hideTile={
                                            index >= initialCount && visibleCount < totalCount
                                        }
                                    />
                                ))}
                            </div>
                            {totalCount > initialCount && (
                                <div
                                    className={classnames(
                                        styles.viewMoreSection,
                                        dibsCss.wFull,
                                        dibsCss.flex,
                                        dibsCss.justifyCenter
                                    )}
                                >
                                    <Button
                                        className={dibsCss.w200px}
                                        buttonRef={viewMoreEl}
                                        size="medium"
                                        type="secondary"
                                        onClick={() => toggleViewMore()}
                                    >
                                        {visibleCount < totalCount ? viewMore : viewLess}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </PageLayout>
                </QuickViewProvider>
            )}
        </FavoritesProvider>
    );
};
